import Card from '$tablet-app/src/app/components/Card';
import Row from '$tablet-app/src/app/components/Row';
import Text from '$tablet-app/src/app/components/Text';
import { Goal } from '@akin-home/goals';
import React, { FC, useMemo } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { LineChart } from 'react-native-chart-kit';
import styled from 'styled-components/native';

interface Props {
  goal: Goal;

  /** Date to show completion rates for */
  date?: Date;

  /** User ID for computing the completion rates */
  user_id: string;

  style?: StyleProp<ViewStyle>;
}

const GoalCompletionStatsCard: FC<Props> = ({ goal, date, user_id, style }) => {
  const targetDate = date || new Date();

  const rate = (goal.getCompletionRate(new Date(), user_id) * 100).toFixed(0);

  const monthlyCompletions = goal.getCompletionsForMonth(targetDate, user_id);
  const toCompleteThisMonth = goal.toCompleteForMonth(targetDate);
  const completionRateForMonth =
    (monthlyCompletions / toCompleteThisMonth) * 100;

  const samples = useMemo(
    () =>
      goal.getCompletionRateSample({
        toDate: targetDate,
        user_id,
        sample_count: 5,
      }),
    [goal, targetDate, user_id]
  );

  const sampleData = samples.length ? samples : [0.5];

  return (
    <StatCard style={style}>
      <Row alignItems="center" justifyContent="space-between" marginBottom={8}>
        <Text variant="caption">Completion Rate</Text>
        <Text weight="semibold">{rate}%</Text>
      </Row>

      <LineChart
        width={236}
        height={56}
        chartConfig={{
          backgroundColor: '#F5F7F9',
          backgroundGradientFrom: '#F5F7F9',
          backgroundGradientTo: '#F5F7F9',
          color: () => `#10B981`,
        }}
        withDots={false}
        fromZero
        withInnerLines={false}
        withOuterLines={false}
        withVerticalLabels={false}
        withHorizontalLabels={false}
        withShadow={false}
        data={{
          labels: [],
          datasets: [
            {
              data: [0.5, ...sampleData],
            },
          ],
        }}
      />

      <Row
        alignItems="center"
        justifyContent="space-between"
        marginTop={16}
        marginBottom={8}
      >
        <Text variant="caption">This month</Text>

        <Text weight="semibold">
          {monthlyCompletions}/{toCompleteThisMonth}
        </Text>
      </Row>

      <MonthProgressBG>
        <MonthProgressBar width={completionRateForMonth} />
      </MonthProgressBG>

      {/* <Text variant="caption">Best Streak</Text> */}

      <GoalName weight="semibold">{goal.title}</GoalName>
    </StatCard>
  );
};

const StatCard = styled(Card)`
  padding: 16px;
`;

const MonthProgressBG = styled.View`
  position: relative;
  height: 10px;
  background-color: #f5f7f9;
  border-radius: 10px;
  width: 100%;
`;

const MonthProgressBar = styled.View<{ width: number }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  border-radius: 10px;
  background-color: #10b981;
  ${({ width }) => `width: ${width}%`}
`;

const GoalName = styled(Text)`
  text-align: center;
  margin-top: 16px;
`;

export default GoalCompletionStatsCard;
